import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download, FileText, Image, Mail } from 'lucide-react';

const PressKit = () => {
  const brandAssets = [
    {
      title: 'AALIGN Logo Pack',
      description: 'Primary logo in PNG, SVG, and EPS formats. Includes light and dark versions.',
      icon: Image,
      type: 'ZIP',
      size: '2.4 MB',
    },
    {
      title: 'Brand Guidelines',
      description: 'Complete brand guidelines including colors, typography, and usage rules.',
      icon: FileText,
      type: 'PDF',
      size: '1.8 MB',
    },
    {
      title: 'Product Screenshots',
      description: 'High-resolution screenshots of the AALIGN dashboard and key features.',
      icon: Image,
      type: 'ZIP',
      size: '8.2 MB',
    },
    {
      title: 'Executive Bios & Photos',
      description: 'Leadership team bios and professional headshots.',
      icon: FileText,
      type: 'ZIP',
      size: '4.5 MB',
    },
  ];

  const brandColors = [
    { name: 'AALIGN Orange', hex: '#F97316', rgb: 'rgb(249, 115, 22)' },
    { name: 'AALIGN Red', hex: '#DC2626', rgb: 'rgb(220, 38, 38)' },
    { name: 'Dark Gray', hex: '#1F2937', rgb: 'rgb(31, 41, 55)' },
    { name: 'Light Gray', hex: '#F3F4F6', rgb: 'rgb(243, 244, 246)' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-br from-orange-50 via-white to-red-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl lg:text-5xl font-black mb-6">Press Kit</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Everything you need to tell the AALIGN story. Download our brand assets, logos, and media resources.
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black mb-6">About AALIGN</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <p className="text-gray-600 leading-relaxed mb-6">
              AALIGN is an AI-powered lead management platform that helps businesses capture, validate, and convert leads more efficiently. Founded with the mission to streamline B2B growth, AALIGN serves 300+ businesses worldwide, processing over 5 million leads.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our platform combines intelligent lead validation, real-time analytics, and seamless CRM integrations to help sales and marketing teams focus on what matters most – building relationships and closing deals.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mt-8 pt-8 border-t border-gray-200">
              <div>
                <p className="text-3xl font-black text-orange-600">300+</p>
                <p className="text-gray-600">Active Businesses</p>
              </div>
              <div>
                <p className="text-3xl font-black text-orange-600">5M+</p>
                <p className="text-gray-600">Leads Processed</p>
              </div>
              <div>
                <p className="text-3xl font-black text-orange-600">99.9%</p>
                <p className="text-gray-600">Uptime</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Assets */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black mb-8">Brand Assets</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {brandAssets.map((asset, idx) => {
              const Icon = asset.icon;
              return (
                <div key={idx} className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-orange-300 hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 mb-1">{asset.title}</h3>
                      <p className="text-gray-600 text-sm mb-3">{asset.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">{asset.type} • {asset.size}</span>
                        <button className="flex items-center gap-2 text-orange-600 font-semibold text-sm hover:text-orange-700">
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Brand Colors */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-black mb-8">Brand Colors</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {brandColors.map((color, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="h-24" style={{ backgroundColor: color.hex }}></div>
                <div className="p-4">
                  <p className="font-bold text-gray-900">{color.name}</p>
                  <p className="text-sm text-gray-600">{color.hex}</p>
                  <p className="text-xs text-gray-500">{color.rgb}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Contact */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white text-center">
            <Mail className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-2">Media Inquiries</h2>
            <p className="text-orange-100 mb-6">
              For press inquiries, interviews, or additional materials, please contact our media team.
            </p>
            <a 
              href="mailto:press@aalign.io" 
              className="inline-block bg-white text-orange-600 font-bold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
            >
              press@aalign.io
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PressKit;
